import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { Document } from 'jsonapi-typescript';
import { Observable, switchMap } from 'rxjs';
import { deserializeEmailValidation } from '../deserializers/email-validation.deserializer';
import { deserializeUser, deserializeUsers } from '../deserializers/user.deserializer';
import { CreateUserPayload } from '../interfaces/create-user-payload.interface';
import { EmailValidationPayload } from '../interfaces/email-validation-payload.interface';
import { EmailValidation } from '../interfaces/email-validation.interface';
import { MetaNew } from '../interfaces/meta-new.interface';
import { UpdateUserPayload } from '../interfaces/update-user-payload.interface';
import { User } from '../interfaces/user.interface';
import { UsersRequestParams } from '../interfaces/users-request-params.interface';
import { serializeEmailValidation } from '../serializers/email-validation.serializer';
import { serializeUpdateUser, serializeUser } from '../serializers/user.serializer';
import { ENVIRONMENT } from '../tokens/environment.token';
import { HttpsParamsBuilderNew } from '../utils/params-builder-new.util';

const USER_INCLUDE = 'grants';

@Injectable({ providedIn: 'root' })
export class UsersService {
	private httpClient = inject(HttpClient);
	private environment = inject(ENVIRONMENT);

	getUsers(params: UsersRequestParams): Observable<{ users: User[]; meta: MetaNew }> {
		const httpParams = HttpsParamsBuilderNew.buildParameters({
			pageNumber: params.pageNumber,
			pageSize: params.pageSize,
			sort: this.buildSortString(params.sort),
			include: params.includeGrants ? USER_INCLUDE : undefined,
			filter: this.buildFilter(params),
		});
		return this.httpClient
			.get<Document>(this.environment.serverNew + '/user/v1/users', { params: httpParams })
			.pipe(switchMap((usersDoc: Document) => deserializeUsers(usersDoc)));
	}

	exportUsersCsv(params: UsersRequestParams = {}): Observable<Blob> {
		const httpParams = HttpsParamsBuilderNew.buildParameters({
			sort: this.buildSortString(params.sort),
			filter: this.buildFilter(params),
		});
		return this.httpClient.get(this.environment.serverNew + '/user/v1/users', {
			params: httpParams,
			headers: { Accept: 'text/csv' },
			responseType: 'blob',
		});
	}

	private buildFilter({
		search,
		role,
		provider,
		idNin,
		idIn,
		roleNin,
		permissionIn,
	}: UsersRequestParams): Record<string, string> {
		const filter: Record<string, string> = {};
		const uniqueIdIn = Array.from(new Set(idIn));
		const uniqueIdNin = Array.from(new Set(idNin));

		if (search) {
			filter['~search'] = search;
		}

		if (role) {
			filter['role'] = role;
		}

		if (provider) {
			filter['provider'] = provider;
		}

		if (uniqueIdNin?.length) {
			filter['id.nin'] = uniqueIdNin.filter((f: string) => !!f).join(',');
		}

		if (uniqueIdIn?.length) {
			filter['id.in'] = uniqueIdIn.filter((f: string) => !!f).join(',');
		}

		if (roleNin?.length) {
			filter['role.nin'] = roleNin.join(',');
		}

		if (permissionIn?.length) {
			filter['grants.permission.in'] = permissionIn.join(',');
		}

		return filter;
	}

	private buildSortString(sort?: Sort): string {
		if (sort?.active && sort?.direction) {
			const direction = sort.direction === 'desc' ? '-' : '';
			return `${direction}${sort.active}`;
		}
		return '';
	}

	getUser(id: string): Observable<User> {
		return this.httpClient
			.get<Document>(this.environment.serverNew + `/user/v1/users/${id}`, {
				params: { include: USER_INCLUDE },
			})
			.pipe(switchMap((doc: Document) => deserializeUser(doc)));
	}

	addUser(user: CreateUserPayload): Observable<CreateUserPayload> {
		return this.httpClient
			.post<Document>(
				this.environment.serverNew + '/user/v1/users',
				serializeUser(user, user.force_local)
			)
			.pipe(switchMap((doc: Document) => deserializeUser(doc)));
	}

	updateUser(user: UpdateUserPayload): Observable<CreateUserPayload> {
		return this.httpClient
			.patch<Document>(
				this.environment.serverNew + `/user/v1/users/${user.id}`,
				serializeUpdateUser(user)
			)
			.pipe(switchMap((doc: Document) => deserializeUser(doc)));
	}

	syncUsersFromEntra(): Observable<any> {
		return this.httpClient.post(this.environment.serverNew + '/user/v1/user-syncs', {});
	}

	checkEmailExists(emailValidation: EmailValidationPayload): Observable<EmailValidation> {
		return this.httpClient
			.post<Document>(
				this.environment.serverNew + '/user/v1/email-validations',
				serializeEmailValidation(emailValidation)
			)
			.pipe(switchMap((doc) => deserializeEmailValidation(doc)));
	}
}
